// Publication window — an honest dual-thumb range control + records-per-year
// histogram (design §5.7), replacing LandscapeMap's old single-thumb
// "Filing window" slider (which could only move the LOWER bound — the upper
// bound was silently pinned to the corpus max the whole time). Two native
// <input type="range"> elements layered in one track is the standard
// dependency-free dual-thumb pattern (amendment G: no new charting/slider
// library) — each thumb owns one bound and z-index/pointer-events keep them
// independently draggable even when their values are close together.
//
// Render-only: every bucket/clamp calculation lives in lib/whitespace-map.ts
// (recordsPerYear, clampPublicationWindow) so it's covered by vitest without
// a DOM — jsdom has no layout, so the histogram bar heights here are
// rendered from already-computed counts, never measured from the DOM.
import type { YearBucket } from '../../lib/whitespace-map'

interface PublicationWindowSliderProps {
  bounds: [number, number]
  value: [number, number]
  onChange: (next: [number, number]) => void
  histogram: YearBucket[]
}

function yearOf(yyyymmdd: number): string {
  const str = String(yyyymmdd)
  return str.length === 8 ? str.slice(0, 4) : str
}

export default function PublicationWindowSlider({ bounds, value, onChange, histogram }: PublicationWindowSliderProps) {
  const [boundsLo, boundsHi] = bounds
  const [lo, hi] = value
  const span = Math.max(boundsHi - boundsLo, 1)
  const maxCount = Math.max(1, ...histogram.map((b) => b.count))

  const pctFor = (yyyymmdd: number) => ((yyyymmdd - boundsLo) / span) * 100
  const inWindow = (year: number) => {
    const yearAsYyyymmdd = year * 10000
    return yearAsYyyymmdd + 9999 >= lo && yearAsYyyymmdd <= hi
  }

  return (
    <div className="mt-3" data-testid="publication-window-slider">
      <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
        <span>{yearOf(boundsLo)}</span>
        <span className="font-medium text-gray-600">
          Publication window: {yearOf(lo)}–{yearOf(hi)}
        </span>
        <span>{yearOf(boundsHi)}</span>
      </div>

      {histogram.length > 0 && (
        <div className="flex items-end gap-px h-8 mb-1" data-testid="publication-window-histogram" aria-hidden="true">
          {histogram.map((bucket) => (
            <div
              key={bucket.year}
              title={`${bucket.year}: ${bucket.count} record${bucket.count === 1 ? '' : 's'}`}
              className={`flex-1 rounded-t-sm transition-colors ${inWindow(bucket.year) ? 'bg-sky-400' : 'bg-gray-200'}`}
              style={{ height: `${Math.max(6, (bucket.count / maxCount) * 100)}%` }}
            />
          ))}
        </div>
      )}

      <div className="relative h-5">
        {/* Static track */}
        <div className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 rounded-full bg-sky-100" />
        {/* Selected-window highlight */}
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-iflow-500"
          style={{ left: `${pctFor(lo)}%`, right: `${100 - pctFor(hi)}%` }}
        />
        <input
          type="range"
          min={boundsLo}
          max={boundsHi}
          value={lo}
          onChange={(e) => onChange([Math.min(Number(e.target.value), hi), hi])}
          className="absolute inset-0 w-full appearance-none bg-transparent accent-iflow-600 pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto"
          aria-label="Publication window — earliest date"
        />
        <input
          type="range"
          min={boundsLo}
          max={boundsHi}
          value={hi}
          onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo)])}
          className="absolute inset-0 w-full appearance-none bg-transparent accent-iflow-600 pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto"
          aria-label="Publication window — latest date"
        />
      </div>
    </div>
  )
}
